import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Star, Clock, CheckCircle, Play, Circle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { getActiveQuests, type QuestWithParticipation } from '../services/api';
import { trackEvent } from '../utils/analytics';

interface QuestSectionProps {
  className?: string;
}

/** Ce que l'élève a fait sur une quête : rien, des copies sous 70, ou au moins une copie à 70 ou plus. */
export function questProgressLabel(quest: QuestWithParticipation): { text: string; tone: 'todo' | 'started' | 'done' } {
  const participation = quest.userParticipation;
  if (!participation?.participated) return { text: 'Pas commencée', tone: 'todo' };
  if (participation.completed) return { text: `Réussie · meilleure note ${participation.bestScore}`, tone: 'done' };
  return { text: `En cours · meilleure note ${participation.bestScore}`, tone: 'started' };
}

const TONE_CLASSES: Record<'todo' | 'started' | 'done', string> = {
  todo: 'text-gray-500 dark:text-gray-400',
  started: 'text-orange-600 dark:text-orange-400',
  done: 'text-green-600 dark:text-green-400',
};

const QuestSection: React.FC<QuestSectionProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [quests, setQuests] = useState<QuestWithParticipation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Polling sur l'identité de l'utilisateur (pas sur l'objet `user`) : un nouvel objet
  // `user` (refreshUser, re-render du provider) ne doit pas relancer un fetch immédiat.
  const userId = user?.id;
  useEffect(() => {
    if (userId) {
      fetchActiveQuests();
      // Poll for updates every 30 seconds
      const interval = setInterval(fetchActiveQuests, 30000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  const fetchActiveQuests = async () => {
    try {
      setQuests(await getActiveQuests());
      setError(null);
    } catch (err) {
      console.error('Error fetching active quests:', err);
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLaunchQuest = (quest: QuestWithParticipation) => {
    trackEvent('quest_launched', { questId: quest.id });
    navigate(`/exercise/quest/${quest.id}`);
  };

  // Don't render anything if no user or still loading initially
  if (!user || isLoading) {
    return null;
  }

  // Don't render if there's an error or no active quest
  if (error || quests.length === 0) {
    return null;
  }

  return (
    <div className={`bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-purple-900/40 dark:to-blue-900/40 border border-amber-200 dark:border-purple-500/30 rounded-lg p-4 ${className} shadow-sm dark:shadow-purple-900/20`}>
      {/* Quest Header */}
      <div className="flex items-center space-x-2">
        <div className="p-2 bg-amber-100 dark:bg-purple-900/50 rounded-lg">
          <Star className="w-6 h-6 text-amber-500 dark:text-purple-400" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {quests.length > 1 ? `Quêtes en cours (${quests.length})` : 'Quête en cours'}
          </h3>
          {quests.length > 1 && (
            <p className="text-xs text-gray-600 dark:text-gray-300">La plus récente en premier.</p>
          )}
        </div>
      </div>

      <ul className="mt-3 space-y-2">
        {quests.map((quest) => {
          const progress = questProgressLabel(quest);
          const ProgressIcon = progress.tone === 'done' ? CheckCircle : progress.tone === 'started' ? Clock : Circle;
          return (
            <li
              key={quest.id}
              data-quest-id={quest.id}
              className="flex flex-wrap items-center justify-between gap-2 bg-white/60 dark:bg-black/20 border border-amber-100 dark:border-purple-500/20 rounded-md px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  <ReactMarkdown components={{
                    p: ({ children }) => <span>{children}</span>,
                    strong: ({ children }) => <strong className="font-bold text-amber-600 dark:text-purple-400">{children}</strong>,
                    code: ({ children }) => <code className="bg-white/50 dark:bg-black/20 px-1 rounded">{children}</code>
                  }}>
                    {quest.title}
                  </ReactMarkdown>
                </p>
                <p className={`mt-0.5 flex items-center gap-1 text-xs font-medium ${TONE_CLASSES[progress.tone]}`}>
                  <ProgressIcon className="w-3.5 h-3.5" />
                  <span>{progress.text}</span>
                </p>
              </div>

              <button
                onClick={() => handleLaunchQuest(quest)}
                className="inline-flex items-center px-3 py-1.5 text-sm bg-amber-600 dark:bg-purple-600 text-white rounded-md hover:bg-amber-700 dark:hover:bg-purple-700 transition-colors shadow-sm"
              >
                <Play className="w-4 h-4 mr-1.5" />
                {progress.tone === 'done' ? 'Refaire la quête' : 'Lancer la quête'}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default QuestSection;
